import { useBaseContext } from "@/components/BaseContext";
import { IconIconInfo } from "@/components/Icons";
import Link from "@/components/Link";
import { studyFormatToString } from "@/server/backend-api/enumToText";
import { Audience, Tag } from "@/server/backend-api/generated";
import { buildCompetenceUrl, buildUrlWithQuery } from "@/utils/url";
import React, { PropsWithChildren } from "react";
import { ChallengePageStaticData } from "../_staticContent/challengePage";
import { BrowseQueryParams } from "../pages/browse";
import {
  MappedLearningOpportunity,
  LearningOpportunityTopic,
} from "../server/backend-api";
import { ContactInfo } from "./Contact/ContactInfo";
import { EciuImage } from "./EciuImage";

const texts = ChallengePageStaticData.metadata;
const fallback = "–";

type ChallengeDefinitionListItemProps = PropsWithChildren<{
  label: string;
  value?: string | null;
  labelClassName?: string;
}>;
const ListItem = (props: ChallengeDefinitionListItemProps) => (
  <div>
    <dt className={props.labelClassName}>{props.label}</dt>
    <dd className="text-style-body2 text-light-yellow">
      {props.children || props.value || fallback}
    </dd>
  </div>
);

const StudyFormatListItem = (props: ChallengeDefinitionListItemProps) => {
  const [hidden, setHidden] = React.useState(true);
  let studyFormatTooltip = "";
  switch (props.value) {
    case "Blended":
      studyFormatTooltip =
        "Learners presence on campus is mandatory for one day up to one week for one part of the course, and online for other parts of the course. Travel funding is often available and subject to the home institution.";
      break;
    case "Hybrid":
      studyFormatTooltip =
        "Learners from partner institutions are not expected to be present on campus. No physical attendance is mandatory, nevertheless learners may be sometime allowed to join in person.";
      break;
    case "Online":
      studyFormatTooltip =
        "Learners from partner institutions are not expected to be present on campus. No physical attendance is mandatory, nevertheless learners may be sometime allowed to join in person.";
      break;
    case "Campus":
      studyFormatTooltip =
        "Learners are expected to be present on the actual campus of the hosting university to attend the ELO.";
      break;
    default:
      studyFormatTooltip = "no info";
      break;
  }
  return (
    <div>
      <dt
        className={
          props.labelClassName === undefined
            ? "text-base font-semibold"
            : props.labelClassName
        }
      >
        {props.label}
      </dt>
      <div
        className="relative cursor-pointer flex gap-x-xs"
        onMouseEnter={() => setHidden(false)}
        onMouseLeave={() => setHidden(true)}
      >
        <dd className="text-style-body2 text-light-yellow">
          {props.children || props.value || fallback}
        </dd>
        {!hidden && (
          <div
            className="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
            style={{ bottom: "100%", left: 0 }}
          >
            {studyFormatTooltip}
          </div>
        )}
        <IconIconInfo className="h-md w-md flex-shrink-0" />
      </div>
    </div>
  );
};

const PageListItem = (props: ChallengeDefinitionListItemProps) => (
  <ListItem {...props} labelClassName="text-base font-semibold" />
);

const ApplyListItem = (props: ChallengeDefinitionListItemProps) => (
  <ListItem {...props} labelClassName="text-style-subtitle1" />
);

const VolumeOfLearningListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    volumeOfLearning: number;
  }
) => (
  <ListItem {...props} label={"Volume of learning"}>
    <p>{`${props.volumeOfLearning} Hours`}</p>
    <p className="text-style-caption text-white flex gap-x-xs pt-xs">
      <IconIconInfo className="h-md w-md flex-shrink-0" />
      <span>No ECTS will be awarded</span>
    </p>
  </ListItem>
);

const EQFLevelListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    eqfLevel: string | null;
  }
) => (
  <ListItem
    {...props}
    labelClassName="text-base font-semibold"
    label={"EQF Level"}
  >
    <p>{props.eqfLevel}</p>
  </ListItem>
);

const TopicListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    topics: LearningOpportunityTopic[];
  }
) => {
  if (props.topics.length === 0) {
    return <ListItem {...props} label={texts.topics} value={"-"} />;
  }
  return (
    <ListItem {...props} label={texts.topics}>
      <div className={"flex flex-col"}>
        {props.topics.map((topic) => {
          return (
            <Link
              key={topic.id}
              href={buildCompetenceUrl(topic.id, topic.name)}
              className="text-style-link2 text-red-100"
            >
              {topic.name}
            </Link>
          );
        })}
      </div>
    </ListItem>
  );
};

const TagListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    tags: Tag[] | null;
  }
) => {
  if (props.tags?.length === 0) {
    return <></>;
  }

  return (
    <ListItem {...props} label={texts.tags}>
      <div className={"flex flex-col"}>
        {props.tags?.map((tag) => {
          const tagUrl = buildUrlWithQuery<BrowseQueryParams>("/browse", {
            tagIds: tag.id.toString(),
          });
          return (
            <Link
              key={tag.id}
              href={tagUrl}
              className="text-style-link1 text-red-100"
            >
              {tag.name}
            </Link>
          );
        })}
      </div>
    </ListItem>
  );
};
const AudiencesListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    audiences: Audience[] | null;
  }
) => {
  return props.audiences !== null && props.audiences.length > 0 ? (
    <ListItem {...props} label={"Learner type"}>
      <div className="flex flex-col gap-y-1  cursor-pointer">
        {props.audiences?.map((audience, idx) => (
          <div
            key={audience.id ?? `${audience.name ?? "audience"}-${idx}`}
            className="relative group flex items-center gap-x-1 w-fit"
          >
            <span>{audience.name ?? fallback}</span>
            <IconIconInfo className="h-md w-md flex-shrink-0" />
            {!!audience.description && (
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 hidden group-hover:block whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white min-w-[12rem] max-w-[20rem]">
                {audience.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </ListItem>
  ) : null;
};
type Props = {
  learningOpp: MappedLearningOpportunity;
};

export const LearningOppPageMetaData = ({ learningOpp }: Props) => {
  const LO_GENERAL_QUESTION = "I have a question about a Learning Opportunity";
  const base = useBaseContext();

  return (
    <dl className="grid grid-cols-1 gap-md">
      <PageListItem label={texts.studyPeriod} value={learningOpp.studyPeriod} />
      <StudyFormatListItem
        label={texts.studyFormat}
        value={studyFormatToString(learningOpp.studyFormat)}
      />
      <PageListItem
        label={texts.applicationPeriod}
        value={learningOpp.applicationPeriod}
      />

      {learningOpp.ects !== null && (
        <PageListItem
          label={texts.ects}
          value={`${learningOpp.ects.toString()} ECTS`}
        />
      )}

      {learningOpp.volumeOfLearning !== null &&
        learningOpp.volumeOfLearning !== 0 && (
          <VolumeOfLearningListItem
            labelClassName="text-base font-semibold"
            volumeOfLearning={learningOpp.volumeOfLearning}
          />
        )}

      <PageListItem
        label={texts.hostingUniversity}
        value={learningOpp.hostingUniversity?.name ?? undefined}
      />
      <TopicListItem
        labelClassName={"text-base font-semibold"}
        topics={learningOpp.topics}
      />
      <AudiencesListItem
        labelClassName={"text-style-subtitle1"}
        audiences={learningOpp.audiences}
      />
      {learningOpp.eqfLevelAlternateName && (
        <EQFLevelListItem
          labelClassName={"text-base font-semibold"}
          eqfLevel={learningOpp.eqfLevelAlternateName}
        />
      )}
      {learningOpp.selfPaced && (
        <div className="font-bold text-xl">Self-paced</div>
      )}
      <TagListItem
        labelClassName={"text-style-subtitle1"}
        tags={learningOpp.tags}
      />
      <div className="bg-white p-4 rounded-3">
        <ContactInfo
          link={false}
          base={base}
          learningOpp={learningOpp}
          LO_GENERAL_QUESTION={LO_GENERAL_QUESTION}
        />
      </div>
    </dl>
  );
};

export const LearningOppApplyMetaData = ({ learningOpp }: Props) => (
  <div className="bg-blue text-white rounded-3 p-lg space-y-lg">
    <div className="flex gap-md items-center">
      <div className="w-[48px] h-[48px]">
        <EciuImage rounded={"3"} src={learningOpp.imageUri} />
      </div>
      <a
        className="text-style-headline5 hover:underline"
        href={learningOpp.url}
      >
        {learningOpp.title}
      </a>
    </div>
    <dl className="grid grid-cols-1 tablet-lg:grid-cols-2 gap-y-md gap-x-3xl">
      {/*TODO: Remove topic text that should not be here.*/}
      <TopicListItem
        labelClassName={"text-style-subtitle1"}
        topics={learningOpp.topics}
      />
      <AudiencesListItem
        labelClassName={"text-style-subtitle1"}
        audiences={learningOpp.audiences}
      />
      <ApplyListItem
        label={texts.studyFormat}
        value={studyFormatToString(learningOpp.studyFormat)}
      />
      <ApplyListItem
        label={texts.applicationPeriod}
        value={learningOpp.applicationPeriod}
      />
      <ApplyListItem
        label={texts.studyPeriod}
        value={learningOpp.studyPeriod}
      />
      {learningOpp.ects !== null && (
        <ApplyListItem
          label={texts.ects}
          value={`${learningOpp.ects.toString()} ECTS`}
        />
      )}
      {learningOpp.volumeOfLearning !== null && (
        <VolumeOfLearningListItem
          labelClassName={"text-style-subtitle1"}
          volumeOfLearning={learningOpp.volumeOfLearning}
        />
      )}
      <ApplyListItem
        label={texts.hostingUniversity}
        value={learningOpp.hostingUniversityName ?? undefined}
      />
      <TagListItem
        labelClassName={"text-style-subtitle1"}
        tags={learningOpp.tags}
      />
      {learningOpp.eqfLevelAlternateName && (
        <EQFLevelListItem
          labelClassName={"text-style-subtitle1"}
          eqfLevel={learningOpp.eqfLevelAlternateName}
        />
      )}
    </dl>
  </div>
);
